import { LitElement, html, PropertyValues } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { CustomRulesSidePanelEvents } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { RuleType } from '../../customRulesGrid/types';
import { nothing } from 'lit';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { Rights } from '@nirvana/shared/constants';

/**
 * @summary Type here the description of this component
 * @tag custom-rules-side-panel
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('custom-rules-side-panel')
export class CustomRulesSidePanelWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({ type: Object, attribute: false, reflect: false })
  rule;

  @property({ type: Object, attribute: false, reflect: false })
  availableCompanies;

  @property({ type: Boolean })
  isGZ = false;

  @property({ type: Number, attribute: true, reflect: true })
  rowIndex;

  @property({ type: Number, attribute: true, reflect: true })
  maxRowIndex;

  @property({ type: String, attribute: false, reflect: false })
  gridType;

  @property({ type: Array, attribute: false, reflect: false })
  targets;

  @property({ type: Array, attribute: false, reflect: false })
  company;

  @property({ type: Array, attribute: false, reflect: false })
  isPartner;

  @property({ type: Boolean, attribute: false, reflect: false })
  showNavigation = true;

  @property({ type: Boolean, attribute: false, reflect: false })
  showActions = true;

  @query('#edit')
  public editButton: GzButtonWebComponent;

  @query('#side-panel_prev-btn')
  private readonly sidePanelPrevButton: GzButtonWebComponent;

  @query('#side-panel_next-btn')
  private readonly sidePanelNextButton: GzButtonWebComponent;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="header mt-6 mb-1">
          ${
            this.showNavigation
              ? html`
                <div class="nav-buttons">
                  <gz-icon-button
                    id="side-panel_prev-btn"
                    fontsize="20"
                    icon="ds-icon-panel_prev"
                    size="24"
                    buttontype="square"
                    buttonfill="quiet"
                    buttoncolor="secondary"
                    @click="${this.getPrevRule}"
                  ></gz-icon-button>
                  <gz-icon-button
                    id="side-panel_next-btn"
                    fontsize="20"
                    icon="ds-icon-panel_next"
                    size="24"
                    buttontype="square"
                    buttonfill="quiet"
                    buttoncolor="secondary"
                    @click="${this.getNextRule}"
                  ></gz-icon-button>
                </div>
              `
              : nothing
          }
          <gz-title class="mb-2" fontsize="16" fontweight="500">${translateFn('Rule details')}</gz-title>
        </div>
        <div class="content pr-1">
          <custom-rules-general
            class="section"
            .availableCompanies=${this.availableCompanies}
            .rule=${this.rule}
            .gridType=${this.gridType}
            .isGZ=${this.isGZ}
            .company=${this.company}
            .isPartner=${this.isPartner}
            .showActions=${this.showActions}
          ></custom-rules-general>
          <custom-rules-rule-outcome
            class="section"
            .rule=${this.rule}
            .gridType=${this.gridType}
            .isGZ=${this.isGZ}
          ></custom-rules-rule-outcome>
          ${this.renderTargets()}
        </div>
        ${this.displayButtons()}
      </div>
    `;
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    if (this.showNavigation) {
      if (this.rowIndex === 0) {
        this.sidePanelPrevButton.disabled = true;
      }

      if (this.rowIndex >= this.maxRowIndex - 1) {
        this.sidePanelNextButton.disabled = true;
      }
    }
  }

  private renderPartnerButtons(footer, buttonsForAllPrivileges, scanButton) {
    const availableCompanies = this.availableCompanies.map(item => item._id);

    let companies;
    if (this.rule.targets.endpointTags) {
      companies = this.rule.targets.endpointTags.map(item => item.companyId);
    } else if (this.rule.targets.companiesIds){
      companies = this.rule.targets.companiesIds;
    } else {
      companies = [this.rule.targets.companyId];
    }

    const unavailableCompanies = companies.filter(item => !availableCompanies.includes(item));
    if (this.rule.canEdit) {
      return footer(buttonsForAllPrivileges);
    }

    if (unavailableCompanies.length < companies.length) {
      return footer(scanButton);
    }
    return nothing;
  }

  private renderGZCloudButtons(footer, buttonsForAllPrivileges, scanButton) {
    const tagCompanies = (this.rule.targets?.endpointTags ?? []).map(item => item.companyId);
    const availableCompanies = this.availableCompanies.map(item => item._id);

    if (availableCompanies.includes(this.rule.companyId)) {
      return footer(buttonsForAllPrivileges);
    }

    if (tagCompanies && tagCompanies.includes(this.company.companyId)) {
      return footer(scanButton);
    }

    return nothing;
  }

  displayButtons() {
    if (!this.showActions
      || !Application.Main.hasAllRights([Rights.RIGHT_MANAGE_INVENTORY, Rights.MANAGE_POLICIES_WRITE])
    ) {
      return nothing;
    }
    const footer = (innerHtml) => html`<div class="footer">${innerHtml}</div>`;
    const scanButton = html`${this.renderScanButton()}`;
    const buttonsForAllPrivileges = html`
      <gz-button id="edit" @click="${this.editRule}">${translateFn('Edit rule')}</gz-button>
      ${scanButton}
      <gz-button class="pl-2" type="outline" color="negative" @click="${this.deleteRule}"
        >${translateFn('Delete')}</gz-button
      >`;
    if (this.isPartner && this.rule) {
      return this.renderPartnerButtons(footer, buttonsForAllPrivileges, scanButton);
    }
    if (!this.isGZ) {
      return this.renderGZCloudButtons(footer, buttonsForAllPrivileges, scanButton);
    }
    return footer(buttonsForAllPrivileges);
  }

  renderScanButton() {
    if (this.rule.type !== RuleType.YARA) {
      return nothing;
    }

    let disabled = false;
    if (this.rule['targets']) {
      if (this.rule['targets']['endpointTags']) {
        disabled = this.rule['targets']['endpointTags'].length === 0;
      }
    }

    return html`
      <gz-button class="pl-2" type="outline" color="primary" @click="${this.scanRule}" .disabled="${disabled}">
        ${translateFn('Scan')}
      </gz-button>
    `;
  }

  renderTargets() {
    if (!this.rule.targets) {
      return;
    }

    return html` <custom-rules-targets
      class="section"
      .targets=${this.targets}
      .rule=${this.rule}
    ></custom-rules-targets>`;
  }

  // see https://lit-element.polymer-project.org/guide/events
  editRule() {
    const event = new CustomEvent(CustomRulesSidePanelEvents.EDIT, {
      detail: {
        rule: this.rule,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  scanRule() {
    const event = new CustomEvent(CustomRulesSidePanelEvents.SCAN, {
      detail: {
        rule: this.rule,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  deleteRule() {
    const event = new CustomEvent(CustomRulesSidePanelEvents.DELETE, {
      detail: {
        rule: this.rule,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  getPrevRule() {
    const event = new CustomEvent(CustomRulesSidePanelEvents.GET_PREV_RULE, {
      detail: {
        rule: this.rule,
        rowIndex: this.rowIndex,
        maxRowIndex: this.maxRowIndex,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  getNextRule() {
    const event = new CustomEvent(CustomRulesSidePanelEvents.GET_NEXT_RULE, {
      detail: {
        rule: this.rule,
        rowIndex: this.rowIndex,
        maxRowIndex: this.maxRowIndex,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'custom-rules-side-panel': CustomRulesSidePanelWebComponent;
  }
}
